import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Plane } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const DestinationSelector = () => {
  const navigate = useNavigate();
  const [destination, setDestination] = useState('');
  const { t } = useTranslation();

  const destinations = [
    { value: '/services/urgent-schengen-visa', label: `🇪🇺 ${t('schengenCountries')}`, flag: '🇪🇺' },
    { value: '/services/usa-urgent-visa', label: `🇺🇸 ${t('unitedStates')}`, flag: '🇺🇸' },
    { value: '/services/urgent-uk-visa', label: `🇬🇧 ${t('unitedKingdom')}`, flag: '🇬🇧' },
    { value: '/services/japan-visa', label: `🇯🇵 ${t('japan')}`, flag: '🇯🇵' },
    { value: '/services/china-visa', label: `🇨🇳 ${t('china')}`, flag: '🇨🇳' },
    { value: '/services/canada-visa', label: `🇨🇦 ${t('canada')}`, flag: '🇨🇦' },
    { value: '/services/russia-visa', label: `🇷🇺 ${t('russia')}`, flag: '🇷🇺' },
    { value: '/services/visa-to-south-africa', label: `🇿🇦 ${t('southAfrica')}`, flag: '🇿🇦' },
  ];

  const handleStartApplication = () => {
    if (destination) {
      navigate(destination);
    }
  };

  return (
    <div className="bg-white rounded-3xl shadow-2xl p-8 backdrop-blur-sm">
      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="text-3xl font-bold text-gray-800 mb-2">
          {t('destinationTitle')}
        </h2>
        <p className="text-gray-600">{t('destinationSubtitle')}</p>
      </div>

      {/* Form */}
      <div className="space-y-4">
        {/* Traveling from */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            {t('travelingFrom')}
          </label>
          <div className="flex items-center space-x-3 p-4 bg-blue-50 border-2 border-teal-100 rounded-xl">
            <span className="text-3xl">🇦🇪</span>
            <div>
              <div className="font-semibold text-gray-800">{t('unitedArabEmirates')}</div>
            </div>
          </div>
        </div>

        {/* Going to */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            {t('goingTo')}
          </label>
          <div className="relative">
            <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <select
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-logo-cyan focus:border-transparent outline-none transition-all text-gray-700 font-medium appearance-none cursor-pointer bg-white"
            >
              <option value="">{t('selectDestination')}</option>
              {destinations.map((dest) => (
                <option key={dest.value} value={dest.value}>
                  {dest.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Start Application Button */}
        <button
          onClick={handleStartApplication}
          disabled={!destination}
          className={`w-full py-4 rounded-full font-semibold transition-all shadow-lg flex items-center justify-center space-x-2 ${
            destination
              ? 'bg-gradient-to-r from-logo-blue to-logo-cyan text-white hover:from-blue-700 hover:to-cyan-700 hover:shadow-xl transform hover:scale-105'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          <Plane className="w-5 h-5" />
          <span>{t('startApplication')}</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mt-8 pt-8 border-t border-gray-200">
        <div className="text-center">
          <div className="text-3xl font-bold bg-gradient-to-r from-logo-blue to-logo-cyan bg-clip-text text-transparent">
            50+
          </div>
          <div className="text-xs text-gray-600 font-medium mt-1">{t('destinations')}</div>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold bg-gradient-to-r from-logo-blue to-logo-cyan bg-clip-text text-transparent">
            25K+
          </div>
          <div className="text-xs text-gray-600 font-medium mt-1">{t('happyTravelers')}</div>
        </div>
        <div className="text-center">
          <div className="text-3xl font-bold bg-gradient-to-r from-logo-blue to-logo-cyan bg-clip-text text-transparent">
            98%
          </div>
          <div className="text-xs text-gray-600 font-medium mt-1">{t('approvalRate')}</div>
        </div>
      </div>
    </div>
  );
};

export default DestinationSelector;
